'use client';

import { Button } from '@common/components';
import { useToast } from '@common/hooks';
import { CustomError } from '@core/utils';
import { captureException } from '@sentry/nextjs';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { AuthQueryOptions } from '@/api';
import { LoadingPageTemplate } from '@/components/client';
import { ResponsiveDialog } from '@/components/organism/ResponsiveDialog';
import { useCheckWelloApp } from '@/hooks/useCheckWelloApp';
import { CUSTOM_SCHEME } from '@/modules/wello-app/_utils/reserved-keywords';
import { useDeviceInfoStore } from '@/stores/DeviceStore';
import { CertificateCard } from './CertificateCard';
interface DonationCertificateDialogProps {
  epayNumber: string;
  opened: boolean;
  onClose: () => void;
}
export const DonationCertificateDialog = ({
  opened,
  epayNumber,
  onClose
}: DonationCertificateDialogProps) => {
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);
  const ref = useRef<HTMLDivElement>(null);
  const {
    toast
  } = useToast();
  const {
    isWelloApp
  } = useCheckWelloApp();
  const viewPortType = useDeviceInfoStore(state => state.viewportType);
  const isMobile = isWelloApp || viewPortType !== 'desktop';
  const {
    data: data_0,
    isLoading,
    isError
  } = useQuery({
    ...AuthQueryOptions['get:/wello/v2/auth/home-donation/epay-no/{epayNo}']({
      path: {
        epayNo: epayNumber
      }
    }),
    enabled: !!epayNumber,
    select: data => data.context
  });
  useEffect(() => {
    if (isError) {
      toast({
        message: '기부증서 정보를 찾을 수 없습니다.',
        type: 'fail'
      });
      onCloseRef.current();
    }
  }, [isError, toast]);
  const handleSaveClick = async () => {
    try {
      if (!data_0) {
        throw new CustomError({
          return_message: '기부증서 정보를 찾을 수 없습니다.'
        });
      }
      if (!ref.current) {
        toast({
          message: '기부증서 저장 실패',
          type: 'fail'
        });
        return;
      }
      const {
        domToPng
      } = await import('modern-screenshot');
      const imageDataUrl = await domToPng(ref.current, {
        scale: 2,
        quality: 1
      });
      if (isMobile) {
        const imagePageHtml = `
        <html>
          <head>
            <title>기부증서</title>
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <style>
              body { 
                margin: 0; 
                padding: 20px; 
                display: flex; 
                flex-direction: column; 
                align-items: center; 
                background-color: #f5f5f5; 
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
              }
          
              img { 
                max-width: 100%; 
                height: auto; 
                border-radius: 8px; 
              }
            </style>
          </head>
          <body>
            <img src="${imageDataUrl}" alt="기부증서" />
          </body>
        </html>`;
        const dataUrl = `data:text/html;charset=utf-8,${encodeURIComponent(imagePageHtml)}`;
        window.location.href = `${CUSTOM_SCHEME.EXTERNAL_PREFIX}${dataUrl}`;
        return;
      }
      const link = document.createElement('a');
      link.href = imageDataUrl;
      link.style.display = 'none';
      link.download = `${data_0.homeInstitutionName}_${data_0.homeDonationBusinessName ? `${data_0.homeDonationBusinessName}_` : ''}기부증서.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast({
        message: '기부증서 저장 완료'
      });
    } catch (error) {
      captureException(error);
      toast({
        message: '기부증서 저장 중 오류가 발생했습니다',
        type: 'fail'
      });
    }
  };
  if (isLoading || !data_0) return <LoadingPageTemplate fadeIn blur />;
  return <ResponsiveDialog modalHeader={{
    title: '기부증서'
  }} opened={opened} onClose={onClose} drawerType="bottom">
      <article className="flex flex-col items-center">
        <CertificateCard ref={ref} name={data_0.userName} paidAt={data_0.paidAt} donationAmount={data_0.donationAmount} className="mb-32" />

        <ul className="mx-auto mb-32 flex w-full max-w-[304px] flex-col gap-8 [&>li]:flex [&>li]:items-start">
          <li>
            <p className="w-[134px] shrink-0 text-start font-400 text-Gray-600 typo-body4">
              지자체
            </p>
            <span className="flex-1 text-start font-400 text-Gray-900 typo-body4">
              {data_0.homeInstitutionName}
            </span>
          </li>
          <li>
            <p className="w-[134px] shrink-0 text-start font-400 text-Gray-600 typo-body4">
              기부명
            </p>
            <span className="flex-1 text-start font-400 text-Gray-900 typo-body4">
              {data_0.homeDonationBusinessName ?? '지자체에 맡김'}
            </span>
          </li>
        </ul>

        <div className="mb-16 w-full bg-Gray-50 px-20 py-12 font-400 text-Gray-550 typo-caption1">
          <ul className="flex flex-col">
            <li>
              * 본 기부내역서는 세액공제 혜택을 위한 법적 효력은 없습니다.
            </li>
            <li>
              * 전자기부금영수증 발급이 필요하시면, 홈택스 &quot; 조회/발급 &gt;
              전자기부금영수증 &gt; 메인화면 메뉴에서 신청이 가능합니다.
            </li>
          </ul>
        </div>

        {!isMobile ? <footer className="w-full">
            <Button className="w-full" onClick={handleSaveClick}>
              다운로드
            </Button>
          </footer> : null}
      </article>
    </ResponsiveDialog>;
};